import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useOutletContext, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { authService } from '@/services/authService'
import { CustomerRegisterForm } from '@/types'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import type { AuthOutletContext } from '@/layouts/AuthLayout'
import { useTranslation } from '@/i18n/useTranslation'

function resolveReturnPath(
  searchParams: URLSearchParams,
  locationState: unknown,
  slug: string | null | undefined,
): string | null {
  const redirect = searchParams.get('redirect')
  if (redirect && redirect.startsWith('/') && !redirect.startsWith('//')) {
    return redirect
  }
  const fromState = (locationState as { from?: { pathname?: string; search?: string } | string } | null)
    ?.from
  if (typeof fromState === 'string' && fromState.startsWith('/')) return fromState
  if (fromState && typeof fromState === 'object' && fromState.pathname) {
    return `${fromState.pathname}${fromState.search || ''}`
  }
  return slug ? `/shop/${slug}` : null
}

const CustomerRegister = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [pendingEmail, setPendingEmail] = useState<string | null>(null)
  const [resending, setResending] = useState(false)
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { t, locale } = useTranslation('common')
  const { shopBranding } = useOutletContext<AuthOutletContext>()
  const { config, primary, shopQuery, slug, loading } = shopBranding
  const shopThemed = Boolean(slug && config)
  const knownShopId = config?.shop_id

  const returnPath = useMemo(
    () => resolveReturnPath(searchParams, location.state, slug),
    [searchParams, location.state, slug],
  )

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CustomerRegisterForm>()

  const password = watch('password')

  useEffect(() => {
    if (knownShopId) {
      setValue('shop_id', knownShopId)
    }
  }, [knownShopId, setValue])

  const onSubmit = async (data: CustomerRegisterForm) => {
    setIsLoading(true)
    try {
      const payload = {
        ...data,
        shop_id: knownShopId || data.shop_id,
        locale,
        redirect_path: returnPath || undefined,
      }
      const response = await authService.customerRegister(payload)

      if (
        response.success &&
        response.data &&
        'pending_email_verification' in response.data &&
        response.data.pending_email_verification
      ) {
        setPendingEmail(response.data.email || data.email)
        toast.success(t('auth.customerRegister.checkEmailToast'))
        return
      }

      toast.error(response.message || t('auth.customerRegister.failed'))
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string; code?: string } } }
      toast.error(err?.response?.data?.message || t('auth.customerRegister.failed'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleResend = async () => {
    if (!pendingEmail || !knownShopId || resending) return
    setResending(true)
    try {
      const res = await authService.resendCustomerVerification({
        email: pendingEmail,
        shop_id: knownShopId,
        locale,
        redirect_path: returnPath || undefined,
      })
      toast.success(res.message || t('auth.customerRegister.resendSent'))
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } }
      toast.error(err?.response?.data?.message || t('auth.customerRegister.resendFailed'))
    } finally {
      setResending(false)
    }
  }

  const linkClass = shopThemed
    ? 'font-medium hover:opacity-85'
    : 'font-medium text-primary-600 hover:text-primary-500'
  const linkStyle = shopThemed ? { color: primary } : undefined
  const submitClass = shopThemed
    ? 'inline-flex w-full items-center justify-center rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-60'
    : 'btn btn-primary btn-lg w-full'
  const submitStyle = shopThemed ? { backgroundColor: primary, color: '#ffffff' } : undefined

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[200px]">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!slug || !knownShopId) {
    return (
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900">{t('auth.customerAuthBlocked.title')}</h2>
        <p className="mt-3 text-sm text-gray-600">{t('auth.customerAuthBlocked.registerBody')}</p>
        <div className="mt-6 space-y-3">
          <div>
            <Link
              to="/auth/staff/login"
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              {t('auth.customerLogin.staffLoginLink')}
            </Link>
          </div>
          <p className="text-xs text-gray-500">{t('auth.customerAuthBlocked.merchantSignupHint')}</p>
          <Link to="/signup" className="text-sm font-medium text-primary-600 hover:text-primary-500">
            {t('auth.customerAuthBlocked.merchantSignupLink')}
          </Link>
        </div>
      </div>
    )
  }

  if (pendingEmail) {
    const continueHref = returnPath?.includes('/checkout')
      ? returnPath.replace(/\/checkout.*/, '') || `/shop/${slug}`
      : returnPath || `/shop/${slug}`

    return (
      <div className="text-center space-y-4">
        <h2 className="text-2xl font-bold text-gray-900">{t('auth.customerRegister.checkEmailTitle')}</h2>
        <p className="text-sm text-gray-600">
          {t('auth.customerRegister.checkEmailBody', { email: pendingEmail })}
        </p>
        <p className="text-xs text-gray-500">{t('auth.customerRegister.checkEmailHint')}</p>
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-center pt-2">
          <button
            type="button"
            onClick={handleResend}
            disabled={resending}
            className={submitClass}
            style={submitStyle}
          >
            {resending ? t('auth.customerRegister.resending') : t('auth.customerRegister.resend')}
          </button>
          <Link
            to={continueHref}
            className="inline-flex items-center justify-center rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {t('auth.customerRegister.continueShopping')}
          </Link>
        </div>
        <p className="text-sm text-gray-600 pt-4">
          {t('auth.customerRegister.alreadyHaveAccount')}{' '}
          <Link to={`/auth/customer/login${shopQuery}`} className={linkClass} style={linkStyle}>
            {t('auth.customerRegister.signInInstead')}
          </Link>
        </p>
      </div>
    )
  }

  return (
    <div>
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">{t('auth.customerRegister.title')}</h2>
        <p className="mt-2 text-sm text-gray-600">{t('auth.customerRegister.subtitle')}</p>
      </div>

      <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
        <input type="hidden" {...register('shop_id', { valueAsNumber: true })} />

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="first_name" className="form-label">
              {t('auth.customerRegister.firstName')}
            </label>
            <input
              {...register('first_name', {
                required: t('auth.customerRegister.firstNameRequired'),
                minLength: { value: 2, message: t('auth.customerRegister.firstNameMin') },
              })}
              type="text"
              className="input"
              placeholder={t('auth.customerRegister.firstNamePlaceholder')}
            />
            {errors.first_name && <p className="form-error">{errors.first_name.message}</p>}
          </div>
          <div>
            <label htmlFor="last_name" className="form-label">
              {t('auth.customerRegister.lastName')}
            </label>
            <input
              {...register('last_name')}
              type="text"
              className="input"
              placeholder={t('auth.customerRegister.lastNamePlaceholder')}
            />
          </div>
        </div>

        <div>
          <label htmlFor="email" className="form-label">
            {t('auth.email')}
          </label>
          <input
            {...register('email', {
              required: t('auth.emailRequired'),
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: t('auth.emailInvalid'),
              },
            })}
            type="email"
            autoComplete="email"
            className="input"
            placeholder={t('auth.emailPlaceholder')}
          />
          {errors.email && <p className="form-error">{errors.email.message}</p>}
        </div>

        <div>
          <label htmlFor="phone" className="form-label">
            {t('auth.customerRegister.phone')}
          </label>
          <input
            {...register('phone')}
            type="tel"
            className="input"
            placeholder={t('auth.customerRegister.phonePlaceholder')}
          />
        </div>

        <div>
          <label htmlFor="password" className="form-label">
            {t('auth.password')}
          </label>
          <input
            {...register('password', {
              required: t('auth.passwordRequired'),
              minLength: { value: 8, message: t('auth.passwordMin') },
            })}
            type="password"
            autoComplete="new-password"
            className="input"
            placeholder={t('auth.passwordPlaceholder')}
          />
          {errors.password && <p className="form-error">{errors.password.message}</p>}
        </div>

        <div>
          <label htmlFor="confirm_password" className="form-label">
            {t('auth.customerRegister.confirmPassword')}
          </label>
          <input
            {...register('confirm_password', {
              required: t('auth.customerRegister.confirmPasswordRequired'),
              validate: (value) =>
                value === password || t('auth.customerRegister.passwordMismatch'),
            })}
            type="password"
            autoComplete="new-password"
            className="input"
            placeholder={t('auth.customerRegister.confirmPasswordPlaceholder')}
          />
          {errors.confirm_password && (
            <p className="form-error">{errors.confirm_password.message}</p>
          )}
        </div>

        <button type="submit" disabled={isLoading} className={submitClass} style={submitStyle}>
          {isLoading ? (
            <span className="inline-flex items-center gap-2">
              <LoadingSpinner size="sm" />
              {t('auth.customerRegister.creating')}
            </span>
          ) : (
            t('auth.customerRegister.submit')
          )}
        </button>
      </form>

      <p className="mt-6 text-center text-sm text-gray-600">
        {t('auth.customerRegister.alreadyHaveAccount')}{' '}
        <Link
          to={`/auth/customer/login${shopQuery}${
            returnPath
              ? `${shopQuery.includes('?') ? '&' : '?'}redirect=${encodeURIComponent(returnPath)}`
              : ''
          }`}
          className={linkClass}
          style={linkStyle}
        >
          {t('auth.customerRegister.signInInstead')}
        </Link>
      </p>
    </div>
  )
}

export default CustomerRegister
